import React, { FC, JSX, useCallback, useRef, useState } from 'react';

import {
	BUTTON_TYPES,
	Copy,
	EvButton,
	EvIconButton,
	EvToast,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { ClipboardHelper } from '../../../helpers/ClipboardHelper';

import './CopyToClipboard.scss';

const copyStates = {
	copying: {
		text: 'Copying. Please wait',
		state: TOAST_STATUS.INFO
	},
	success: {
		text: 'Copied to Clipboard',
		state: TOAST_STATUS.SUCCESS
	},
	error: {
		text: 'Copy Attempt Error',
		state: TOAST_STATUS.FAIL
	}
};

interface ICopyToClipboardProps {
	textToCopy: string;
	renderJustIcon?: boolean;
	copyButtonText?: string;
	iconTitle?: string;
	buttonType?: BUTTON_TYPES;
	dataAutomationId?: string;
}

type copyStateItem = {
	text: string;
	state: TOAST_STATUS;
};

const CopyToClipboard: FC<ICopyToClipboardProps> = ({
	renderJustIcon,
	copyButtonText = 'Copy Issue Link',
	textToCopy,
	iconTitle = 'Copy selector',
	buttonType = BUTTON_TYPES.SECONDARY,
	dataAutomationId
}) => {
	const [copyState, setCopyState] = useState<copyStateItem>(copyStates.copying);
	const [showAnnouncement, setShowAnnouncement] = useState<boolean>(false);
	const [buttonRef, setbuttonRef] = useState(useRef(null));

	const copyTextLinkButton = useRef(null);

	const onToastClose = (): void => {
		setShowAnnouncement(false);
		buttonRef.current.focus();
	};

	const renderToast = (): JSX.Element => (
		<EvToast
			announcement={copyState.text}
			status={copyState.state}
			show={showAnnouncement}
			onToastClose={onToastClose}
		/>
	);

	const copyToClipboard = (text): void => {
		const isCopied = ClipboardHelper.copyToClipboard(text);
		setCopyState(isCopied ? copyStates.success : copyStates.error);
		setShowAnnouncement(true);
	};

	const copyButtonSelector = useCallback(
		(copyToClipboardButton: React.RefObject<HTMLButtonElement>): void => {
			setbuttonRef(copyToClipboardButton);
			copyToClipboard(textToCopy);
		},
		[textToCopy]
	);

	const copyTextLink = useCallback((): void => {
		setbuttonRef(copyTextLinkButton);
		copyToClipboard(textToCopy);
	}, [textToCopy]);

	return (
		<div className="copy-to-clipboard">
			{showAnnouncement && renderToast()}
			<div id="announce" className="sr-only" aria-live="assertive">
				{copyState.text !== copyStates.copying.text && <p>{copyState.text}</p>}
			</div>
			<div className="copy-text">
				{renderJustIcon ? (
					<EvIconButton
						ref={buttonRef}
						icon={Copy}
						isSmall
						ariaLabel={iconTitle}
						onClick={() => copyButtonSelector(buttonRef)}
					/>
				) : (
					<EvButton
						ref={copyTextLinkButton}
						type={buttonType}
						onClick={copyTextLink}
						dataAutomationId={dataAutomationId}
					>
						{copyButtonText}
					</EvButton>
				)}
			</div>
		</div>
	);
};

export { CopyToClipboard };
